import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../redux/reduxhooks';
import { SocialEnum } from '../../types/auth';
import { MarketPlaceCandidateFormData as FormCandidateFormData } from '../../types/candidate';
import { authService } from '../../lib/api/auth';
import { localStorageGetItem, localStorageRemoveItem } from '../../components/storage/localStorageManager';
import { sessionStorageClearAll } from '../../components/storage/sessionStorageManager';
import { goToRoom } from '../registration/utils';
import { isCaptchaEnabled } from '../../config/constants';
import { useKYCDigioSignFlow, useUserProfile } from '../../hooks/useSelector';
import { isKYCDigioSignFeatureEnabled } from '../../lib/utils/common-functions';
import MarketPlaceCandidateRegistrationForm from './MarketPlaceCandidateSignUp';
import LeftPanelContent from '../../components/registration/LeftPanelContent';
import SignUpHeader from '../../components/registration/SignUpHeader';
import { useCandidateEmail } from '../../hooks/useMarketPlaceIntegration';
import PageLoader from '../../components/loader';
import { toast } from 'react-toastify';
import { routes } from '../routes/routes-names';
import CreatePassword from '../../components/registration/CreatePassword';
import Swal from 'sweetalert2';

const MarketPlaceBaseFormLayout: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const kycDigioSignFlowSettings = useKYCDigioSignFlow();
    const reduxProfile = useUserProfile();
    const [_, marketPlaceIdValue] = useLocation().search.split('=');
    const customEmailFromStorage = localStorageGetItem('Email') || '';
    const passString = localStorageGetItem('Password') || '';
    const [step, setStep] = useState('set-password');
    const [password, setPassword] = useState('');

    const { data, isLoading } = useCandidateEmail(marketPlaceIdValue);

    const { Email, Phone, FirstName, LastName, MiddleName, Country, Dob } = data || {};

    const userEmail = Email ?? customEmailFromStorage;
    const showCompleteProfilePage = userEmail.endsWith('@gmail.com') || userEmail.endsWith('@outlook.com');

    const captchaEnabled = String(isCaptchaEnabled).toLowerCase() === 'true' ? true : false;

    const handleSubmit = async (values: FormCandidateFormData, email: string) => {
        const requestData = {
            ...values,
            FirstName: FirstName ?? values.FirstName,
            LastName: LastName ?? values.LastName,
            MiddleName: MiddleName ?? values.MiddleName,
            Phone: Phone ?? values.Phone,
            Email: (email || Email) ?? customEmailFromStorage,
            CityDistrict: values.CityDistrict === '' ? null : values.CityDistrict,
            country: Country ?? (values.country === '' ? '' : values.country),
            state: values.state === '' ? null : values.state,
            Gender: Number(values.Gender),
            CandidateAddress: '%%' + values.PostalCode,
            Dob: Dob ?? (values.Dob as Date),
            AuthType: passString ? SocialEnum.CustomAuth : reduxProfile?.authType || SocialEnum.CustomAuth,
            Password: password ?? (passString || ''),
            Token: reduxProfile?.token || '',
            HighestEducation: values.HighestEducation === '' ? null : Number(values.HighestEducation),
            IsCandidateTermsChecked: true,
            ExperienceLevel: null,
            JobTitle: '',
            PrevCompany: '',
            Aadhar: '',
            flatAddress: '',
            streetAddress: '',
        };

        try {
            const data = await authService.candidateSignUp(requestData);
            if (data) {
                sessionStorageClearAll();
                await goToRoom(data, dispatch, navigate);
            }
            localStorageRemoveItem('Email');
        } catch (error) {
            console.error('Registration failed:', error);
            throw error;
        }
    };

    const [formProps, setFormProps] = useState({
        onSubmit: handleSubmit,
        captchaEnabled,
        isKYCRequired: isKYCDigioSignFeatureEnabled(kycDigioSignFlowSettings),
        customEmail: Email ?? (customEmailFromStorage || ''),
        marketPlaceData: data,
    });

      useEffect(() => {
        if (data?.isAlreadyRegistered) {
            Swal.fire({
                title: 'Already Registered',
                text: 'You are already registered with OfferX. Please login using your email and password.',
                icon: 'info',
                confirmButtonText: 'OK',
            }).then(() => {
                navigate('/registration/social');
            });
        }
    }, [data, navigate]);

    useEffect(() => {
        if (userEmail) {
            if (showCompleteProfilePage) {
                setStep('complete-profile');
            } else {
                setStep('set-password');
            }
        }
    }, [Email, customEmailFromStorage]);

    useEffect(() => {
        if (Email === null && marketPlaceIdValue) {
            toast.error('You have already registered via this link.');
            navigate(routes.main);
        } else {
            setFormProps({ ...formProps, marketPlaceData: data });
        }
    }, [Email, marketPlaceIdValue, navigate, data]);

    if (isLoading) {
        return <PageLoader />;
    }

    return (
        <>
            <SignUpHeader />
            <div className="container mt-4">
                <div className="row screen-height d-flex align-items-center ms-lg-2  ms-md-2 ms-0">
                    <LeftPanelContent showTestimonial={false} signAs={'candidate'} columnSize={4} />
                    <div className="col-lg-8 order-lg-2 order-md-2 order-1">
                        {step !== 'set-password' && !showCompleteProfilePage && (
                            <div className="my-3">
                                <span className="pe-2 text-secondary">
                                    <span
                                        className="cursor-pointer"
                                        onClick={() => {
                                            setStep('set-password');
                                        }}
                                    >
                                        <i className="bi bi-chevron-left lt-text-secondary-alt me-0"></i> Back
                                    </span>
                                </span>
                            </div>
                        )}
                        <div className="col-md-12">
                            <div className="tab-content" id="nav-tabContent">
                                <div className="tab-pane fade show active" id="nav-home" role="tabpanel">
                                    <div className="mx-auto d-block">
                                        {step === 'set-password' ? (
                                            <CreatePassword
                                                submitPassowrdData={(passwordValue) => {
                                                    setPassword(passwordValue);
                                                }}
                                                userEmail={userEmail}
                                                setStep={setStep}
                                            />
                                        ) : (
                                            <MarketPlaceCandidateRegistrationForm {...formProps} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MarketPlaceBaseFormLayout;
